/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 */

/**
 * Evolve — remove the dark lazyload "shimmer" sweep.
 * The theme's image placeholder (.Image__Loader) paints a solid black ::after band at
 * 200% width that sweeps across each image while it loads. On the large homepage hero
 * images this reads as a big dark shape flashing on every page load. We keep the neutral
 * light-grey placeholder but drop the dark sweep so there's no flash. Reversible: delete
 * this block to restore the theme default.
 */
(function () {
  try {
    var css = '.Image__Loader::after{display:none !important;}';
    var style = document.createElement('style');
    style.setAttribute('data-evolve', 'lazyload-shimmer-off');
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  } catch (e) {}
})();

/**
 * Evolve — predictive search.
 * Wires live results into two existing inputs: the desktop top-bar search
 * (.Header__TopBarSearchInput, in sections/header.liquid) and the full-screen
 * mobile search modal (#Search .Search__Input, in layout/theme.liquid).
 * Uses Shopify's native Predictive Search API (/search/suggest.json) — no
 * template/view wiring required. Renders into #HeaderPredictiveSearchResults
 * and #ModalPredictiveSearchResults, which must exist in the markup.
 * Reversible: delete this block (and the matching markup/CSS) to remove.
 */
(function () {
  var ENDPOINT = '/search/suggest.json';
  var DEBOUNCE_MS = 250;
  var MIN_CHARS = 2;
  var RESULT_LIMIT = 6;
  var activeController = null;

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatMoney(decimalAmount) {
    var amount = parseFloat(decimalAmount);
    if (isNaN(amount)) return '';
    var currency = (window.theme && window.theme.shopCurrency) || 'AUD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function fetchPredictiveResults(term) {
    if (activeController) activeController.abort();
    activeController = ('AbortController' in window) ? new AbortController() : null;

    var url = ENDPOINT + '?q=' + encodeURIComponent(term) +
      '&resources[type]=product' +
      '&resources[limit]=' + RESULT_LIMIT +
      '&resources[options][unavailable_products]=last';

    return fetch(url, activeController ? { signal: activeController.signal } : {})
      .then(function (res) { return res.json(); });
  }

  function renderResults(container, term, data) {
    var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];

    if (!products.length) {
      container.innerHTML = '<p class="PredictiveSearch__Empty Text--subdued">No results for &ldquo;' + escapeHtml(term) + '&rdquo;</p>';
      container.classList.add('is-visible');
      return;
    }

    var html = '<ul class="PredictiveSearch__List" role="listbox">';

    products.forEach(function (product) {
      html += '<li class="PredictiveSearch__Item">' +
        '<a href="' + product.url + '" class="PredictiveSearch__Link" role="option">' +
        (product.image
          ? '<img class="PredictiveSearch__Image" src="' + product.image + '" alt="" width="48" height="48" loading="lazy">'
          : '<span class="PredictiveSearch__ImagePlaceholder"></span>') +
        '<span class="PredictiveSearch__Info">' +
        '<span class="PredictiveSearch__Title">' + escapeHtml(product.title) + '</span>' +
        '<span class="PredictiveSearch__Price">' + formatMoney(product.price) + '</span>' +
        '</span>' +
        '</a>' +
        '</li>';
    });

    html += '</ul>';
    html += '<a class="PredictiveSearch__ViewAll" href="/search?q=' + encodeURIComponent(term) + '">View all results for &ldquo;' + escapeHtml(term) + '&rdquo;</a>';

    container.innerHTML = html;
    container.classList.add('is-visible');
  }

  function closeResults(container, input) {
    container.classList.remove('is-visible');
    container.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
  }

  function initPredictiveSearch(input, container) {
    if (!input || !container) return;

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', container.id);
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocomplete', 'off');

    var handleInput = debounce(function () {
      var term = input.value.trim();

      if (term.length < MIN_CHARS) {
        closeResults(container, input);
        return;
      }

      fetchPredictiveResults(term).then(function (data) {
        renderResults(container, term, data);
        input.setAttribute('aria-expanded', 'true');
      }).catch(function (err) {
        if (err && err.name === 'AbortError') return;
        closeResults(container, input);
      });
    }, DEBOUNCE_MS);

    input.addEventListener('input', handleInput);

    input.addEventListener('keydown', function (e) {
      var items = container.querySelectorAll('.PredictiveSearch__Link');
      if (!items.length) return;

      var currentIndex = -1;
      for (var i = 0; i < items.length; i++) {
        if (items[i].classList.contains('is-focused')) { currentIndex = i; break; }
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        var next = items[currentIndex + 1] || items[0];
        for (var j = 0; j < items.length; j++) items[j].classList.remove('is-focused');
        next.classList.add('is-focused');
        next.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        var prev = items[currentIndex - 1] || items[items.length - 1];
        for (var k = 0; k < items.length; k++) items[k].classList.remove('is-focused');
        prev.classList.add('is-focused');
        prev.scrollIntoView({ block: 'nearest' });
      } else if (e.key === 'Enter') {
        var focused = container.querySelector('.PredictiveSearch__Link.is-focused');
        if (focused) {
          e.preventDefault();
          window.location.href = focused.getAttribute('href');
        }
      } else if (e.key === 'Escape') {
        closeResults(container, input);
        input.blur();
      }
    });

    input.addEventListener('blur', function () {
      // delay so a click/mousedown on a result isn't lost to the blur firing first
      setTimeout(function () {
        closeResults(container, input);
      }, 150);
    });

    document.addEventListener('click', function (e) {
      if (container.classList.contains('is-visible') && !container.contains(e.target) && e.target !== input) {
        closeResults(container, input);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initPredictiveSearch(
      document.querySelector('.Header__TopBarSearchInput'),
      document.getElementById('HeaderPredictiveSearchResults')
    );

    initPredictiveSearch(
      document.querySelector('#Search .Search__Input'),
      document.getElementById('ModalPredictiveSearchResults')
    );
  });
})();
